import { Injectable } from '@angular/core';
import {
  CommoditiesService,
  LocalesService,
  ReferenceService,
  TimezonesService,
  TypeService,
  SiteService,
  OrganizationsService,
  ServiceDeliveryPointService,
  ExternalPointsService,
  UtilityAccountsService,
} from '@services';
import { SupportingDataService } from 'src/app/shared/services/supporting-data.service';
import { ContextSelectorService } from 'ngx-global-nav';
import { combineLatest } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ServiceDeliveryPointFacade {
  readonly sites$ = this.siteService.sites$;
  readonly loadingSites$ = this.siteService.loadingSites$;
  readonly loadingOrgs$ = this.organizationsService.loadingOrgs$;
  readonly loadingExternalPoints$ = this.externalPointsService.loadingExternalPoints$;
  readonly externalPoints$ = this.externalPointsService.externalPoints$;
  readonly loadingServiceDeliveryPoint$ = this.serviceDeliveryPointService.loadingServiceDeliveryPoint$;
  readonly serviceDeliveryPoint$ = this.serviceDeliveryPointService.serviceDeliveryPoint$;
  readonly serviceDeliveryPoints$ = this.serviceDeliveryPointService.serviceDeliveryPoints$;
  readonly serviceAccounts$ = this.serviceDeliveryPointService.serviceAccounts$;

  readonly types$ = this.typeService.types$;
  readonly commodities$ = this.commoditiesService.commodities$;
  readonly locales$ = this.localesService.locales$;
  readonly utilities$ = this.utilitiesService.utilities$;

  get timezones() {
    return this.timezonesService.timezones;
  }

  readonly dataProviders$ = this.supportingDataService.dataProviders$;
  readonly channels$ = this.supportingDataService.channels$;
  readonly reportingIntervals$ = this.supportingDataService.reportingIntervals$;

  readonly currentContext$ = this.orgSelectorService.currentContext$;

  readonly externalPointsReady$ = combineLatest([this.externalPoints$, this.loadingExternalPoints$]).pipe(
    map(([pts, loading]) => !loading && Array.isArray(pts)),
    distinctUntilChanged(),
  );

  readonly isAddExternalPointDisabled$ = combineLatest([
    this.loadingOrgs$,
    this.loadingSites$,
    this.loadingExternalPoints$,
    this.loadingServiceDeliveryPoint$,
    this.externalPointsReady$,
  ]).pipe(
    map(
      ([loadingExt, loadingOrg, loadingSite, loadingSdp, ready]) =>
        loadingExt || loadingOrg || loadingSite || loadingSdp || !ready,
    ),
    debounceTime(120),
    distinctUntilChanged(),
  );

  constructor(
    private supportingDataService: SupportingDataService,
    private commoditiesService: CommoditiesService,
    private typeService: TypeService,
    private localesService: LocalesService,
    private timezonesService: TimezonesService,
    private utilitiesService: ReferenceService,
    private siteService: SiteService,
    private organizationsService: OrganizationsService,
    private serviceDeliveryPointService: ServiceDeliveryPointService,
    private externalPointsService: ExternalPointsService,
    private utilityAccountsService: UtilityAccountsService,
    private orgSelectorService: ContextSelectorService,
  ) {}

  async initializeLookups(): Promise<void> {
    try {
      await Promise.all([
        this.supportingDataService.getChannels(),
        this.supportingDataService.fetchChannelWhitelists(),
        this.supportingDataService.setReportingIntervals(),
        this.commoditiesService.setCommodities(),
        this.typeService.setTypes(),
        this.localesService.setLocales(),
        this.timezonesService.setTimezones(),
      ]);

      await this.supportingDataService.setDataProviders();

      await this.utilitiesService.setUtilities();
    } catch (error) {
      console.error('Error initializing lookups:', error);
      throw error;
    }
  }

  getSelectedSite() {
    return this.siteService.getSelectedSite();
  }

  setSelectedSite(siteId: string) {
    this.siteService.setSelectedSite(siteId);
  }

  selectSite(siteId: string) {
    this.siteService.selectSite(siteId);
  }

  checkForLocale(localeName: string, locales: any[]) {
    return this.localesService.checkForLocale(localeName, locales);
  }

  mapServiceAccountsToCards(accounts: any[]) {
    return this.utilityAccountsService.mapServiceAccountsToCards(accounts);
  }

  getExternalPointsChannels() {
    return this.supportingDataService.getExternalPointsChannels();
  }

  async getServiceDeliveryPoint(id: string) {
    return this.serviceDeliveryPointService.getServiceDeliveryPoint(id);
  }

  async getParentSiteId(orgId: string, sdpId: string) {
    return this.serviceDeliveryPointService.getParentSiteId(orgId, sdpId);
  }

  setSdpParentId(siteId: string) {
    this.serviceDeliveryPointService.setSdpParentId(siteId);
  }

  async getExternalPoints(siteId: string, type: string, sdpId: string) {
    return this.externalPointsService.getExternalPoints(siteId, type, sdpId);
  }

  setLoadingExternalPoints(loading: boolean) {
    this.externalPointsService.loadingExternalPoints$.next(loading);
  }

  setExternalPoints(points: any[]) {
    this.externalPointsService.externalPoints$.next(points);
  }

  getLoadingExternalPointsValue() {
    return this.externalPointsService.loadingExternalPoints$.getValue();
  }

  getServiceDeliveryPointValue() {
    return this.serviceDeliveryPointService.serviceDeliveryPoint$.getValue();
  }

  getServiceDeliveryPointsValue() {
    return this.serviceDeliveryPointService.serviceDeliveryPoints$.getValue();
  }
}
